import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, Select, message, Spin, Progress } from "antd";
import {
  DownloadOutlined,
  UploadOutlined,
  FileExcelOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  InboxOutlined,
} from "@ant-design/icons";
import { useClinics } from "../../hooks/useClinics";
import drEasyApi from "../../Api/Customapi";

/**
 * BulkInvoiceModal
 *
 * Props:
 *  visible   – boolean
 *  onCancel  – () => void
 *  onSuccess – () => void  (refetch bookings after upload)
 *  isMobile  – boolean
 */
const BulkInvoiceModal = ({ visible, clinicId,onCancel, onSuccess, isMobile }) => {
  const { clinicsQuery } = useClinics({ pageNumber: 1, pageSize: 100 });
console.log(clinicId);

  const [selectedClinicId, setSelectedClinicId] = useState(clinicId);
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [uploadResult, setUploadResult] = useState(null); // null | { success, message }
  const fileInputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setUploadResult(null);
    setSelectedClinicId(null);
  };

  const handleClose = () => {
    reset();
    onCancel?.();
  };
console.log(clinicId);
useEffect(() => {
  if (clinicId) {
    setSelectedClinicId(clinicId);
 } }, [clinicId]);

  // ── Download Template ──────────────────────────────────────────────────────
  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const res = await drEasyApi.get("/api/Invoice/BulkTemplate", {
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Invoice_Bulk_Template.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      message.success("Template downloaded!");
    } catch {
      message.error("Failed to download template");
    } finally {
      setDownloadingTemplate(false);
    }
  };

  // ── File selection ─────────────────────────────────────────────────────────
  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;
    const isExcel =
      selectedFile.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      selectedFile.type === "application/vnd.ms-excel" ||
      selectedFile.name.endsWith(".xlsx") ||
      selectedFile.name.endsWith(".xls");
    if (!isExcel) {
      message.error("Please upload an Excel file (.xlsx or .xls)");
      return;
    }
    setFile(selectedFile);
    setUploadResult(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files[0];
    handleFileSelect(dropped);
  };

  // ── Upload ─────────────────────────────────────────────────────────────────
  const handleUpload = async () => {
    if (!file) { message.warning("Please select a file first"); return; }
    if (!selectedClinicId) { message.warning("Please select a clinic"); return; }

    setUploading(true);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append("File", file);
      formData.append("ClinicId", selectedClinicId);

      await drEasyApi.post("/api/Invoice/BulkCreate", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setUploadResult({ success: true, message: "Invoices created successfully!" });
      message.success("Bulk invoices created!");
      onSuccess?.();
    } catch (err) {
      const errMsg =
        err.response?.data?.message ||
        err.response?.data?.[0] ||
        "Upload failed. Please check the file format.";
      setUploadResult({ success: false, message: errMsg });
      message.error(errMsg);
    } finally {
      setUploading(false);
    }
  };

  const canUpload = file && selectedClinicId && !uploading;

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={isMobile ? "94vw" : 500}
      centered
      title={
        <div className="flex items-center gap-2">
          <FileExcelOutlined className="text-green-600 text-lg" />
          <span className="font-semibold text-gray-800"> Invoices To   {clinicsQuery.data?.clinics?.find((c) => c.clinicId === selectedClinicId) && (
            <span className="md:text-xl text-primary" key={selectedClinicId} value={selectedClinicId}>
              {clinicsQuery.data?.clinics?.find((c) => c.clinicId === selectedClinicId)?.clinicName}
            </span>
      )}</span>
        </div>
      }
      destroyOnClose
    >
      {/* ── Step 1: Download Template ── */}
      <div className="mb-5">
        
        <Button
          icon={<DownloadOutlined />}
          loading={downloadingTemplate}
          onClick={handleDownloadTemplate}
          block
          size="large"
          style={{ borderColor: "#16a34a", color: "#16a34a" }}
          className="rounded-lg font-medium"
        >
          Download Excel Template
        </Button>
      </div>

    

      {/* ── Step 3: Upload File ── */}
      <div className="mb-5">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
          Step 3 — Upload filled file
        </p>

        {/* Drop zone */}
        <div
          className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all duration-200
            ${dragging ? "border-primary bg-primary/5 scale-[1.01]" : "border-gray-200 hover:border-primary hover:bg-gray-50"}
            ${file ? "border-green-400 bg-green-50" : ""}
          `}
          onClick={() => !file && fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          {file ? (
            <div className="flex items-center justify-between px-2">
              <div className="flex items-center gap-3">
                <FileExcelOutlined className="text-green-600 text-2xl" />
                <div className="text-left">
                  <p className="font-medium text-gray-800 text-sm truncate max-w-[220px]">
                    {file.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
              <Button
                type="text"
                size="small"
                danger
                onClick={(e) => { e.stopPropagation(); setFile(null); setUploadResult(null); }}
              >
                Remove
              </Button>
            </div>
          ) : (
            <div>
              <InboxOutlined className="text-3xl text-gray-400 mb-2" />
              <p className="text-sm text-gray-600 font-medium">
                Drag & drop or <span className="text-primary underline cursor-pointer">browse</span>
              </p>
              <p className="text-xs text-gray-400 mt-1">.xlsx or .xls files only</p>
            </div>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          className="hidden"
          onChange={(e) => handleFileSelect(e.target.files?.[0])}
        />
      </div>

      {/* ── Result feedback ── */}
      {uploadResult && (
        <div className={`flex items-start gap-2 p-3 rounded-lg mb-4 text-sm
          ${uploadResult.success ? "bg-green-50 border border-green-200 text-green-700" : "bg-red-50 border border-red-200 text-red-600"}`}
        >
          {uploadResult.success
            ? <CheckCircleFilled className="mt-0.5 shrink-0" />
            : <CloseCircleFilled className="mt-0.5 shrink-0" />}
          <span>{uploadResult.message}</span>
        </div>
      )}

      {/* ── Actions ── */}
      <div className="flex gap-3">
        <Button block size="large" onClick={handleClose} className="border-gray-300 text-gray-600 rounded-lg">
          Close
        </Button>
        <Button
          block
          size="large"
          type="primary"
          icon={<UploadOutlined />}
          loading={uploading}
          disabled={!canUpload}
          onClick={handleUpload}
          style={{ backgroundColor: "#298d9f", borderColor: "#298d9f" }}
          className="rounded-lg font-medium"
        >
          {uploading ? "Uploading..." : "Upload & Create"}
        </Button>
      </div>
    </Modal>
  );
};

export default BulkInvoiceModal;